import { messages, type RenderJobDto } from '@app/shared';
import type { Dictionary } from '../i18n';
import { creditsCount } from './format';
import { stageWords, tellAction } from './render-format';

/** What the build screen shows (spec 6.2 «Сборка», «Результат»). */
export type RenderBlock =
  | { kind: 'waiting'; text: string }
  | { kind: 'queued'; text: string }
  | { kind: 'running'; words: string; percent: number }
  | { kind: 'done' }
  | {
      kind: 'failed';
      /** The same words as the chat's message. */
      text: string;
      /** The engine's own words when the text above does not carry them. */
      engineMessage: string | null;
      refunded: string | null;
      action: { label: string; to: string };
    };

export function renderBlock(job: RenderJobDto, t: Dictionary): RenderBlock {
  switch (job.status) {
    // «Собрать» was pressed before the voice was ready: the server voices, then queues it.
    case 'waiting':
      return { kind: 'waiting', text: t.render.waiting };
    case 'queued':
      return { kind: 'queued', text: t.render.queued(job.queueAhead) };
    case 'running':
      return { kind: 'running', words: stageWords(job.stage, t), percent: Math.round((job.progress ?? 0) * 100) };
    case 'done':
      return { kind: 'done' };
    case 'failed': {
      const m = messages();
      const engine = job.refusal?.message ?? '';
      const [text, carriesEngine] =
        job.tell === 'text'
          ? [m.renderFailedText(engine), true]
          : job.tell === 'own_voice'
            ? [m.renderFailedOwnVoice(engine), true]
            : job.tell === 'voice'
              ? // A waiting build whose take failed or lost a piece of the text: the chat's words.
                [
                  job.error === 'voice_gross'
                    ? m.renderVoiceTakeGross
                    : job.error === 'voice_failed'
                      ? m.renderVoiceTakeFailed
                      : m.renderFailedVoice,
                  false,
                ]
              : job.tell === 'later'
                ? [m.renderFailedLater, false]
                : [m.renderFailedSorry, false];
      return {
        kind: 'failed',
        text,
        engineMessage: !carriesEngine && engine ? engine : null,
        refunded: job.refunded > 0 ? t.render.refunded(creditsCount(job.refunded)) : null,
        action: tellAction(job, t),
      };
    }
  }
}

/** The screen asks again every 3 s while the build waits or works. */
export function shouldPoll(job: Pick<RenderJobDto, 'status'> | undefined): boolean {
  return job?.status === 'waiting' || job?.status === 'queued' || job?.status === 'running';
}

/** The tile picture in «Готовое»: the cover, else the first image of the result, else none (a format placeholder). */
export function readyCover(job: Pick<RenderJobDto, 'coverUrl' | 'files'>): string | null {
  return job.coverUrl ?? job.files.find((f) => f.kind === 'image')?.url ?? null;
}
